import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { MButton } from "@/components/ui/atoms/MButtons";
import {
  IconPlus,
  IconX,
  IconFolderPlus,
  IconBellPlus,
  IconFilePlus,
  IconBubblePlus,
  IconActivity,
} from "@tabler/icons-react";
import styles from "./MFab.module.css";
import { useNavigate, useLocation } from "react-router-dom";
import { MDocumentUploadOptions } from "@/components/ui/molecules/MDocumentUploadOptions";
import { getMemberInfo } from "@/hooks/useMembers";
import { trackUserDataLoaded } from "@/utils/gtm";
import {
  ANALYTICS_SCREEN_TYPES,
  ANALYTICS_SCREEN_NAMES,
} from "@/constants/analytics.constants";
import AddVitalFlow from "@/pages/vitals-tracker/AddVitalFlow";

const MFab: React.FC = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [showDocModal, setShowDocModal] = useState(false);
  const [showVitalFlow, setShowVitalFlow] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const loggedInMember = getMemberInfo(true);

  const ctas = [
    {
      icon: <IconActivity className={styles.ctaIcon} />,
      text: t("vitalsTracker.logAVital"),
      key: "vitals",
    },
    {
      icon: <IconFolderPlus className={styles.ctaIcon} />,
      text: t("fab.addDocument"),
      key: "document",
    },
    {
      icon: <IconBellPlus className={styles.ctaIcon} />,
      text: t("fab.addReminder"),
      key: "reminder",
    },
    {
      icon: <IconBubblePlus className={styles.ctaIcon} />,
      text: t("fab.addHealthMemory"),
      key: "health",
    },
    {
      icon: <IconFilePlus className={styles.ctaIcon} />,
      text: t("fab.addFamilyNote"),
      key: "note",
    },
  ];

  const handleCtaClick = (key: string) => {
    setOpen(false);
    switch (key) {
      case "vitals":
        setShowVitalFlow(true);
        break;
      case "document":
        // Track document add method click
        trackUserDataLoaded({
          memberInfo: loggedInMember,
          screenType: ANALYTICS_SCREEN_TYPES.DOCUMENTS,
          screenName: ANALYTICS_SCREEN_NAMES.DOCUMENTS_ADD_METHOD,
        });
        setShowDocModal(true);
        break;
      case "reminder":
        navigate("/reminders?mode=add", {
          state: { backUrl: location.pathname },
        });
        break;
      case "health":
        navigate("/documents/health-memory/add", {
          state: { backUrl: location.pathname },
        });
        break;
      case "note":
        navigate("/profile/family/notes?mode=add", {
          state: { backUrl: location.pathname },
        });
        break;
      default:
        break;
    }
  };

  return (
    <>
      {showDocModal && (
        <MDocumentUploadOptions
          open={showDocModal}
          onClose={() => setShowDocModal(false)}
        />
      )}
      {showVitalFlow && (
        <AddVitalFlow
          open={showVitalFlow}
          onClose={() => setShowVitalFlow(false)}
        />
      )}
      {open && (
        <div className={styles.mfabBackdrop} onClick={() => setOpen(false)} />
      )}
      <div className={`${styles.mfabRoot} ${open ? styles.open : ""}`}>
        <div className={styles.ctaList}>
          {ctas.map((cta) => (
            <div
              className={styles.ctaRow}
              key={cta.key}
              onClick={() => handleCtaClick(cta.key)}
            >
              <div className={styles.ctaText}>{cta.text}</div>
              <button
                className={styles.ctaCircle}
                aria-label={cta.text}
                tabIndex={open ? 0 : -1}
                type="button"
              >
                {cta.icon}
              </button>
            </div>
          ))}
        </div>

        <button
          className={styles.fabButton}
          onClick={() => setOpen((o) => !o)}
          aria-label={open ? "Close" : "Add"}
          data-fab-button
        >
          <MButton
            variant="icon-only-filled"
            size="md"
            className={styles.fabInnerButton}
            icon={
              open ? (
                <IconX size={16} stroke={2} />
              ) : (
                <IconPlus size={16} stroke={2} />
              )
            }
            state="default"
          />
        </button>
      </div>
    </>
  );
};

export default MFab;
